import { useState, useEffect, memo } from 'react';
import { Flex, Button, Icon } from '@chakra-ui/react';
import Select, { components } from 'react-select';
import { Check } from '@kula-ai/icons/outline/Check';
import { Copy03 } from '@kula-ai/icons/dual-tone/Copy03';
import { KText } from '@/ui-library/components/text';
import { colors } from '@/theme/foundations/colors';

interface OptionHeaderProps {
  onChange?: (value: any) => void;
  clickToCopy?: () => void;
  headerOptions: Array<{ label: string; value: any }>;
  toShowCopy?: boolean;
  disableCopy?: boolean;
  initialValue: any;
}

interface CustomOptionProps {
  data: { label: string; value: any };
  isSelected: boolean;
  [key: string]: any;
}

interface CustomSingleValueProps {
  children: React.ReactNode;
  [key: string]: any;
}

const customStyles = {
  container: (provided) => ({
    ...provided,
    width: '100%',
  }),
  control: (provided) => ({
    ...provided,
    width: '100%',
    height: '32px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 'semiBold',
    borderColor: colors['secondary-100'],
    boxShadow: 'none',
    '&:hover': { borderColor: colors['secondary-200'] },
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '12px',
    backgroundColor: state.isSelected
      ? colors['primary-25']
      : colors['base-white'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&:hover': {
      backgroundColor: colors['primary-25'],
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '8px',
    marginTop: '4px',
  }),
};

const OptionHeader = ({
  onChange = (value: Object) => {},
  clickToCopy = () => {},
  headerOptions,
  toShowCopy = false,
  disableCopy = false,
  initialValue = headerOptions[0],
}: OptionHeaderProps) => {
  const [value, setValue] = useState(initialValue);
  const [copyToggle, setCopyToggle] = useState(false);

  useEffect(() => {
    onChange(value);
  }, [onChange, value]);

  const copyToClipboard = () => {
    setCopyToggle(true);
    clickToCopy();
    setTimeout(() => setCopyToggle(false), 2000);
  };

  return (
    <Flex justifyContent="space-between" alignItems="center" w="100%">
      <Flex flex={1}>
        <Select
          value={value}
          isSearchable={false}
          options={headerOptions}
          isLoading={false}
          styles={{
            ...customStyles,
            control: (provided: any) => ({
              ...customStyles.control(provided),
              borderTopRightRadius: toShowCopy ? '0px' : '8px',
              borderBottomRightRadius: toShowCopy ? '0px' : '8px',
            }),
          }}
          onChange={(options) => {
            setValue(options);
          }}
          components={{
            IndicatorSeparator: null,
            Option: CustomOption,
            SingleValue: CustomSingleValue,
          }}
        />
      </Flex>
      {toShowCopy && (
        <Button
          variant="quaternary"
          onClick={copyToClipboard}
          minW="190px"
          borderLeftRadius="none"
          minH="38px"
          borderLeft="none"
          bg="secondary-25"
          disabled={disableCopy}
          _disabled={{
            border: '1px solid',
            borderColor: 'border-color',
            borderLeft: 'none',
            cursor: 'not-allowed',
            _hover: {},
            opacity: 0.5,
          }}
        >
          {!copyToggle ? (
            <Icon as={Copy03} w="16px" h="16px" mr="8px" />
          ) : (
            <Icon as={Check} w="16px" h="16px" mr="8px" />
          )}
          {!copyToggle ? 'Copy' : 'Copied'} to clipboard
        </Button>
      )}
    </Flex>
  );
};

const CustomOption = ({ ...props }: CustomOptionProps) => {
  return (
    <components.Option {...props}>
      <Flex alignItems="center" flex="1">
        <KText size="sm-regular">{props.data.label}</KText>
      </Flex>
      {props.isSelected && (
        <Icon as={Check} w="16px" h="16px" color="primary-500" />
      )}
    </components.Option>
  );
};

const CustomSingleValue = ({ children, ...props }: CustomSingleValueProps) => (
  <components.SingleValue {...props}>
    <KText>{children}</KText>
  </components.SingleValue>
);

export default memo(OptionHeader);
